import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshingRef = useRef(false);

  // Attempt to refresh access token using httpOnly refresh cookie
  const attemptTokenRefresh = useCallback(async () => {
    if (refreshingRef.current) return false;
    refreshingRef.current = true;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        return true;
      }
    } catch (e) {
      console.error('Token refresh failed:', e);
    } finally {
      refreshingRef.current = false;
    }
    return false;
  }, []);

  // Hydrate auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // On activation pages, the token arrives in the URL query string.
        // Save it to localStorage BEFORE calling /api/auth/me so we don't
        // trigger a 401 → removeItem race condition.
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        if (urlToken && window.location.pathname.includes('/activate')) {
          localStorage.setItem('token', urlToken);
        }

        const storedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
          headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else if (response.status === 401) {
          // Access token expired — try refresh
          const refreshed = await attemptTokenRefresh();
          if (refreshed) {
            const retryToken = localStorage.getItem('token');
            const retry = await fetch(`${API_URL}/api/auth/me`, {
              credentials: 'include',
              headers: retryToken ? { 'Authorization': `Bearer ${retryToken}` } : {}
            });
            if (retry.ok) {
              const userData = await retry.json();
              setUser(userData);
            } else {
              localStorage.removeItem('token');
            }
          } else {
            localStorage.removeItem('token');
          }
        }
      } catch (error) {
        console.error('Auth init error:', error);
        localStorage.removeItem('token');
      }
      setLoading(false);
    };
    initAuth();
  }, [attemptTokenRefresh]);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
      signal: AbortSignal.timeout(30000)
    });

    let data;
    try { data = await response.clone().json(); } catch { data = {}; }

    if (!response.ok) {
      throw new Error(data.detail || 'Credenciales inválidas');
    }

    // Server sets httpOnly cookies (access + refresh).
    // localStorage token kept as cross-origin fallback.
    const accessToken = data.token || data.access_token;
    if (accessToken) {
      localStorage.setItem('token', accessToken);
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (userData) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
      credentials: 'include'
    });

    let data;
    try { data = await response.clone().json(); } catch { data = {}; }

    if (!response.ok) {
      throw new Error(data.detail || 'Error al registrarse');
    }

    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    setUser(data.user);
    return data.user;
  };

  const refreshAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else if (response.status === 401) {
        // Try refresh
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          const retryToken = localStorage.getItem('token');
          const retry = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include',
            headers: retryToken ? { 'Authorization': `Bearer ${retryToken}` } : {}
          });
          if (retry.ok) {
            const userData = await retry.json();
            setUser(userData);
            return userData;
          }
        }
      }
    } catch (e) {}
    return null;
  }, [attemptTokenRefresh]);

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {}
    localStorage.removeItem('token');
    // Clear leftover guest meeting creds so they can't hijack a later staff/client join
    localStorage.removeItem('guest_token');
    localStorage.removeItem('guest_name');
    setUser(null);
  };

  // Google OAuth stubs — feature incomplete, no backend endpoint
  const loginWithGoogle = () => {
    console.warn('[Auth] Google OAuth not implemented');
    throw new Error('Google OAuth no está disponible en este momento');
  };
  const handleGoogleCallback = async () => {
    console.warn('[Auth] Google OAuth callback not implemented');
    throw new Error('Google OAuth no está disponible en este momento');
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    refreshAuth,
    attemptTokenRefresh,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
